/**
 * Expenses the company always incurs, worked out rather than typed.
 *
 * Every rule here was stated by the owner. They are computed on read, not
 * written as rows: general liability is a percentage of revenue, so a row saved
 * at one contract value would be silently wrong the moment a line item changed.
 * A rep can still add ordinary expenses by hand; these sit alongside them and
 * always reflect the estimate as it stands.
 *
 * The GC fee is the odd one out. It is a cost *and* it is added to the contract
 * at cost — the company passes it through rather than marking it up — so it
 * appears here and again as revenue in the ladder. Net effect on gross profit:
 * nothing. See `computeLadder`.
 */

import {
  type Cents,
  addCents,
  cents,
  multiplyCents,
  percentOf,
  toCents,
  toPercent,
} from "./money.js";
import {
  INSURED_SUB_GL_PER_1000,
  type InsuredCrew,
  type OrderInsurance,
  type TradeList,
  orderInsurance,
  perHundred,
  perThousand,
  tradeList,
} from "./insurance.js";
import { todayCentral } from "./partners.js";

/** Roof types that need a lift on site. Chosen when the estimate is created. */
export const ROOF_TYPES = [
  "Shingle",
  "Standing Seam Metal",
  "Exposed Fastener Metal Panel",
  "Flat / Low Slope",
  "Other",
] as const;
export type RoofType = (typeof ROOF_TYPES)[number];

const LIFT_ROOF_TYPES: readonly string[] = [
  "Standing Seam Metal",
  "AG Panel 26 Gauge Residential",
  "R Panel 26 Gauge Residential",
  // What the two above were called until August 2026. Kept so an estimate
  // written under the old name still budgets its lift — a roof type is free
  // text on the document, and the document is the record of what was agreed.
  "Exposed Fastener Metal Panel",
];

/**
 * Crews whose work orders carried no subcontractor charge, until 2026-09-05.
 *
 * Kept only as the record of who the rule named. Everyone on this list was
 * said to carry their own workers comp, which the `crews` table now records
 * per crew with an expiry date beside it — see `insurance.ts`. Nothing reads
 * these names any more, and nothing should: a name in a list cannot expire.
 */
export const EXEMPT_CREW_PATTERNS: readonly string[] = [
  "elvis",
  "go remodeling",
  "pablo",
  "c&y gutters",
  "eth roofing",
  "coyote gutters",
  "luis jimenez",
  "martin tellez",
  "second generation masonry",
  "smith roofing",
];

/**
 * The blended 13.17% that stood in for both policies until 2026-09-05.
 *
 * One percentage for every crew, charged whether the company carried their
 * comp or not. The real rates arrived with policy BAS-83535-1 and the binding
 * meeting on 2026-09-01, and they are nothing like one number: an insured
 * crew costs 0.7% and an uninsured roofer costs 12.535% plus 7.35% of comp.
 * The constant stays as the documented origin of the old figures on old
 * estimates. `insurance.ts` charges the real ones.
 */
export const SUBCONTRACTOR_PCT = 13.17;

/**
 * The 1% of revenue general liability used to cost, until 2026-09-05.
 *
 * It was never a rate anybody was charged; it was a round number that put
 * roughly the right money aside. The carrier rates liability on what the crew
 * is paid and at a different rate per trade, so the charge now comes out of
 * `insurance.ts` per work order. Kept for the same reason as
 * `SUBCONTRACTOR_PCT`: it explains every General Liability line written before
 * that date.
 */
export const GENERAL_LIABILITY_PCT = 1;
export const PERMIT_PCT = 1;
/**
 * The floor under a permit, raised from $100 on 2026-08-31.
 *
 * Jonathon: "it's 1% for $150 minimum. which is higher." Replacements only,
 * residential and commercial alike — he was asked whether a commercial repair
 * should pull one too and said to leave repairs alone. A new construction
 * roof pulls the same one (Jonathon, 2026-09-30: "Add the permit by itself,
 * the same as a replacement: 1% of the price, $150 at least." ADR 0223).
 */
export const PERMIT_MINIMUM = 150_00 as Cents;

/**
 * Mountain Brook's permit fee, as its pink application prints it (ADR 0289):
 * "Residential-$11.00 per $1000 Commercial-$12.00 per $1000 plus a $50.00
 * issuance fee MINIMUM FEE OF $100.00) and a $20.00 State Trade Academy flat
 * fee". The minimum is inside the city's brackets, so it holds the per-$1,000
 * part and the issuance fee together, and the $20 goes on top: $120 at least.
 * A part thousand counts as a whole one (Jonathon, 2026-10-16: "Round up to
 * next $1,000").
 */
export const MOUNTAIN_BROOK_PERMIT = {
  residentialPer1000: 11_00 as Cents,
  commercialPer1000: 12_00 as Cents,
  issuance: 50_00 as Cents,
  minimum: 100_00 as Cents,
  stateTradeAcademy: 20_00 as Cents,
} as const;

/** Mountain Brook's fee on a job of this size, and how it was worked out. */
export function mountainBrookPermitFee(
  revenue: Cents,
  jobType: string | null | undefined,
): { amount: Cents; basis: string } {
  const fee = MOUNTAIN_BROOK_PERMIT;
  const commercial = isCommercialJob(jobType);
  const per1000 = commercial ? fee.commercialPer1000 : fee.residentialPer1000;
  const thousands = Math.ceil(Math.max(0, revenue) / 1000_00);
  const city = Math.max(thousands * per1000 + fee.issuance, fee.minimum);
  return {
    amount: cents(city + fee.stateTradeAcademy),
    basis:
      `Mountain Brook: $${per1000 / 100} per $1,000 ` +
      `(${commercial ? "commercial" : "residential"}, ${thousands} ` +
      `thousand${thousands === 1 ? "" : "s"}) plus $50 issuance, $100 ` +
      `minimum, plus $20 State Trade Academy`,
  };
}
/**
 * The manufacturer warranty tiers, priced per square including waste.
 *
 * Silver Pledge was once the only one, so it is the default for anything that
 * does not say — every estimate written before the tiers existed keeps the
 * number it already had.
 *
 * Golden Pledge is no longer sold: GAF only files one for a Master Elite
 * contractor and CopperRidge is not one yet (Jonathon, 2026-09-21), so no
 * template carries the tier any more (ADR 0194). It stays here because an
 * estimate written under it has to go on pricing the way it was priced.
 *
 * TAMKO's is the Titan XT roof's: "tamko warranty is $7 a square in other
 * expenses" (Jonathon, 2026-10-01, ADR 0256). The same money as Silver Pledge,
 * named for the manufacturer whose roof it is.
 *
 * Registration is Standard Architectural's: "make sure you add a $5 per
 * square other expense to register the warranty" (Jonathon, 2026-10-01), on
 * that roof only — Titan XT's $7 stands alone.
 */
export const WARRANTY_TIERS = [
  "silver_pledge",
  "golden_pledge",
  "tamko",
  "registration",
  "none",
] as const;
export type WarrantyTier = (typeof WARRANTY_TIERS)[number];

export const SILVER_PLEDGE_PER_SQUARE = 7_00 as Cents;
export const GOLDEN_PLEDGE_PER_SQUARE = 10_00 as Cents;
export const TAMKO_WARRANTY_PER_SQUARE = 7_00 as Cents;
export const WARRANTY_REGISTRATION_PER_SQUARE = 5_00 as Cents;

const WARRANTY: Record<
  WarrantyTier,
  { name: string; perSquare: Cents } | null
> = {
  silver_pledge: {
    name: "Silver Pledge Warranty",
    perSquare: SILVER_PLEDGE_PER_SQUARE,
  },
  golden_pledge: {
    name: "Golden Pledge Warranty",
    perSquare: GOLDEN_PLEDGE_PER_SQUARE,
  },
  tamko: {
    name: "TAMKO MasterShield Warranty",
    perSquare: TAMKO_WARRANTY_PER_SQUARE,
  },
  registration: {
    name: "TAMKO ProShield Warranty",
    perSquare: WARRANTY_REGISTRATION_PER_SQUARE,
  },
  none: null,
};

export function warrantyTier(value: unknown): WarrantyTier {
  return WARRANTY_TIERS.includes(value as WarrantyTier)
    ? (value as WarrantyTier)
    : "silver_pledge";
}
/**
 * A lift, and how much roof one of them covers.
 *
 * Flat at $2,000 until 2026-08-31, when Jonathon put a size behind it: "you can
 * keep 2k. the math can be for every 75 squares." A 200-square building is
 * three lifts, not one, and before this it was budgeted as one.
 *
 * Never less than one. A commercial job small enough to round to nothing still
 * has a lift standing in the car park.
 */
export const LIFT_COST = 2_000_00 as Cents;
export const LIFT_PER_SQUARES = 75;

/**
 * A dumpster, and how much tear-off fills one.
 *
 * Two changes on 2026-08-31: $1,000 to $2,000, and it stopped being flat.
 * Jonathon: "dumpster also needs to go on other expenses for 2k per 40
 * squares", and asked whether layers counted, "yes" — so the base is what comes
 * off the roof rather than the size of the roof. Sixty squares with three
 * layers on it is 180 squares of debris and five dumpsters.
 *
 * Still never less than one, and that is the whole reason the floor is here:
 * new construction and a flute fill tear nothing off, and he was asked directly
 * whether those still get a dumpster. They do — there is packaging and offcut
 * on a roof nobody stripped.
 */
export const DUMPSTER_COST = 2_000_00 as Cents;
export const DUMPSTER_PER_SQUARES = 40;

/** How many lifts a roof this size needs. Rounded up, never below one. */
export function liftCount(squares: number): number {
  const roof = Number(squares);
  if (!Number.isFinite(roof) || roof <= 0) return 1;
  return Math.max(1, Math.ceil(roof / LIFT_PER_SQUARES));
}

/** How many dumpsters this much debris fills. Rounded up, never below one. */
export function dumpsterCount(debris: number): number {
  const squares = Number(debris);
  if (!Number.isFinite(squares) || squares <= 0) return 1;
  return Math.max(1, Math.ceil(squares / DUMPSTER_PER_SQUARES));
}
/**
 * The crane on a commercial job.
 *
 * Jonathon, 2026-08-26: "for commercial jobs, a crane needs to be an option in
 * other expenses. it's 276 an hour with an 8 hour minimum a day. 9% fuel fee
 * and 12% miscellaneous fee to the total. 230 squares will take three weeks so
 * please factor that in."
 *
 * An option rather than an automatic, which is what separates it from the lift
 * and the dumpster above: those go on every commercial job, and a crane goes on
 * the ones that need one. Nothing is charged until somebody turns it on.
 *
 * What is worked out is how long it stands there. Three weeks of five-day weeks
 * is fifteen days for 230 squares, so fifteen days is what 230 squares costs
 * and the roof scales the rest. Confirmed by Jonathon on 2026-08-26: the three
 * weeks are Monday to Friday, not calendar weeks.
 *
 * Both fees are taken on the hire, not on each other — "to the total", and the
 * total is the hours. Compounding them would quietly add another $23.85 a day
 * — $357.75 across the reference job — which is exactly the kind of number
 * nobody notices on an invoice. `autoExpenses.test.ts` pins the trap.
 */
export const CRANE_HOURLY_RATE = 276_00 as Cents;
/** Billed by the day whatever the crew does with it, so eight hours is a day. */
export const CRANE_MINIMUM_HOURS_PER_DAY = 8;
export const CRANE_FUEL_PCT = 9;
export const CRANE_MISC_PCT = 12;
/**
 * The reference job the rate of work comes from, kept as the two numbers he
 * gave rather than the division of them. 15.333 squares a day is nobody's
 * rule; "230 squares is three weeks" is, and it is still legible in five years.
 */
export const CRANE_REFERENCE_SQUARES = 230;
export const CRANE_REFERENCE_DAYS = 15;

/** What a day of crane costs before the two fees. */
export const CRANE_DAY_RATE = multiplyCents(
  CRANE_HOURLY_RATE,
  CRANE_MINIMUM_HOURS_PER_DAY,
);

/**
 * How many days of crane a roof this size takes.
 *
 * Rounded up, because a crane is hired by the day and half a day of it is a
 * day. Never less than one: a job small enough to work out to nothing still
 * has the crane on site, and returning zero would put a $0 line on the
 * estimate that reads as "no crane" while the toggle says otherwise.
 */
export function craneDaysForSquares(squares: number): number {
  const roof = Number(squares);
  if (!Number.isFinite(roof) || roof <= 0) return 1;
  return Math.max(
    1,
    Math.ceil((roof * CRANE_REFERENCE_DAYS) / CRANE_REFERENCE_SQUARES),
  );
}

/** The hire plus its fuel and miscellaneous fees. */
export function craneCost(days: number): Cents {
  const hire = multiplyCents(CRANE_DAY_RATE, Math.max(0, Number(days) || 0));
  return addCents(
    hire,
    percentOf(hire, toPercent(CRANE_FUEL_PCT)),
    percentOf(hire, toPercent(CRANE_MISC_PCT)),
  );
}

export const GC_FEE_PCT = 2;
/**
 * The floor under the GC fee, lowered from $1,000 on 2026-08-31, when the
 * $50,000 revenue threshold above it was removed altogether.
 *
 * Jonathon: "gc fee is required on all jobs. 2% of total revenue. 500 minimum."
 * Asked whether "all jobs" reached residential, he said "all commercial jobs" —
 * so the commercial test in `gcFee` stays and only the threshold goes. A small
 * commercial job used to carry no fee at all, which is the case this fixes: the
 * general contractor takes their cut on a $12,000 job the same as a $120,000
 * one.
 */
export const GC_FEE_MINIMUM = 500_00 as Cents;

/** One computed expense line. `key` is stable so the UI can list them. */
export interface AutoExpense {
  key: string;
  name: string;
  amount: Cents;
  /** Shown under the name — why this line is here. */
  basis: string;
  /**
   * Struck off this job by a person. The row is still here on purpose: there
   * is nothing to delete — the expense is a calculation, not a saved row — so
   * "gone" is a fact recorded beside it, and a row that vanished from the list
   * entirely could never be put back. `totalAutoExpenses` skips it; the screen
   * shows it struck through with a way to restore it.
   */
  removed?: boolean;
  /**
   * Something about this line that needs a person, shown beside it.
   *
   * Only insurance uses it so far: a crew whose certificate is missing or out
   * of date is budgeted as carrying nothing, and the estimate says so rather
   * than quietly charging the expensive rate — the certificate is worth real
   * money and somebody can still go and get it (ADR 0198).
   */
  warning?: string;
  /**
   * `estimate_insurance_lines.id`, on the rows a person added by hand.
   *
   * The estimate screen shows those rows in the table with the sub and the
   * labour on them — Jonathon, 2026-09-22: "i'd rather it just be natural
   * like the work order and material order where you just add an item and
   * make the selection there". Carried here rather than read back out of
   * `key`, so the screen never has to parse a string the domain owns.
   */
  insuranceLineId?: string;
}

export interface AutoExpenseInput {
  /** The contract total before the GC fee is added back. */
  revenue: Cents;
  /** 'residential' or 'commercial'. */
  jobType: string | null | undefined;
  /** The estimate's trade, e.g. "Roofing Replacement". */
  trade: string | null | undefined;
  roofType: string | null | undefined;
  /** Which manufacturer warranty this job carries. Defaults to Silver Pledge. */
  warranty?: string | null | undefined;
  /**
   * Whether this estimate hires a crane. Commercial work only — see the rate
   * constants above.
   */
  crane?: boolean | null | undefined;
  /**
   * Days of crane, when somebody knows better than the roof does. Null, zero or
   * anything unreadable leaves it to `craneDaysForSquares`.
   */
  craneDays?: unknown;
  /** Squares and waste from the job's roof layouts. */
  layouts: readonly {
    squares?: unknown;
    waste_pct?: unknown;
    /** How many layers are coming off. Zero on new construction. */
    layers?: unknown;
  }[];
  /**
   * Work orders, with what is needed to rate the insurance on each: what the
   * crew is paid, what trade the work is, and what the crew's papers say.
   */
  workOrders: readonly AutoExpenseWorkOrder[];
  /**
   * Insurance added by hand, for subs with no work order — see
   * `AutoExpenseInsuranceLine`. Each one produces the same pair of lines a
   * work order would, at the same rates.
   */
  insuranceLines?: readonly AutoExpenseInsuranceLine[] | undefined;
  /**
   * `YYYY-MM-DD`, for reading crew certificates against. Defaults to today in
   * Central time — the estimate screens do not pass one, and the tests do so
   * an expiry never depends on the day the suite runs.
   */
  today?: string | undefined;
  /**
   * Amounts somebody has typed over the calculated ones, keyed by expense key.
   * A key that is absent, null or blank keeps the calculation.
   */
  overrides?: Readonly<Record<string, unknown>> | null | undefined;
  /**
   * Expenses struck off this job, keyed by expense key. A truthy value means
   * somebody said this job does not have this expense at all — see
   * `applyRemovals`.
   */
  removed?: Readonly<Record<string, unknown>> | null | undefined;
  /**
   * Charge the permit at Mountain Brook's own fee rather than 1%. Decided by
   * `mountainBrookFeeApplies` from the job's city and when the estimate was
   * made (ADR 0289).
   */
  mountainBrookPermit?: boolean | undefined;
  /**
   * What Mountain Brook's fee is worked out on, when it is not `revenue`: what
   * the customer signed, once they have (ADR 0289). The 1% never reads it.
   */
  permitRevenue?: Cents | undefined;
}

export interface AutoExpenseWorkOrder {
  /**
   * `work_orders.crew_id`. What a hand-added insurance line is matched
   * against — never the name, which two crews can share and one crew can
   * be renamed out of. See `crewsWithBoth`.
   */
  crewId?: string | null | undefined;
  /**
   * `crews.name`, the company, exactly as stored. The saved key of the
   * crew's liability and comp lines is built on it, and estimates already
   * hold overrides, strike-offs and payment types under those keys, so it
   * must stay the stored name — never the on-screen label, which changes the
   * moment somebody fills in a contact (ADR 0265).
   */
  crewName: string | null | undefined;
  /**
   * How the crew reads on screen — "Apex Metal (Brad Kiley)", see
   * `crewLabel`. Used for the line's name and warning only. Falls back to
   * `crewName`.
   */
  crewLabel?: string | null | undefined;
  total: Cents;
  /** The trades on the order, which choose the insurance class. */
  trades?: TradeList;
  /** The crew's insurance columns, straight off `crews`. */
  crew?: InsuredCrew | null;
}

/**
 * Insurance somebody asked for by hand, for a sub with no work order yet.
 *
 * Jonathon, 2026-09-15: "gl and wc also need to be options to select on other
 * expenses based on whether or not the sub has them. that way they are
 * products that are automatically calculated based on the sub in case we're
 * working on estimates that don't generate them."
 *
 * Both policies are rated on what the crew is paid, and until now the only
 * thing on an estimate that says what a crew is paid is a work order. So an
 * estimate priced before anybody raised one carried no liability at all — not
 * a small one, none — and the margin on screen was wrong by the whole of it.
 * This is the way to say "Pablo is doing $12,000 of this" before the order
 * exists.
 *
 * What it does *not* do is let anybody type the insurance. The amount is still
 * `orderInsurance` on the crew's own certificates, exactly as a work order's
 * is: the rate is the company's business, the labour is the job's.
 */
export interface AutoExpenseInsuranceLine {
  /** The row's id. The expense key is built on it, so it must be stable. */
  id: string;
  /** `estimate_insurance_lines.crew_id`. See `crewsWithBoth`. */
  crewId?: string | null | undefined;
  crewName: string | null | undefined;
  /** What the crew is expected to be paid. */
  paid: Cents;
  /** The trade, when it is not the estimate's own. */
  trades?: TradeList;
  /** The crew's insurance columns, straight off `crews`. */
  crew?: InsuredCrew | null;
}

/**
 * Whether a job is commercial.
 *
 * Exported because the screens ask it too — the crane switch is only offered on
 * a commercial job, and "what counts as commercial" is a rule rather than a
 * string comparison somebody re-types in a component.
 */
export const isCommercialJob = (jobType: string | null | undefined): boolean =>
  (jobType ?? "").toLowerCase() === "commercial";

const isCommercial = isCommercialJob;

const isRoofReplacement = (trade: string | null | undefined): boolean =>
  (trade ?? "").toLowerCase() === "roofing replacement";

/**
 * Whether the trade is a new build, matched on a word because a trade is free
 * text that is added and never renamed (rule 8). "Roofing New Construction"
 * today (ADR 0223). The Mountain Brook permit reads it too, to tick "New
 * Construction" (ADR 0289).
 */
export const isNewConstruction = (trade: string | null | undefined): boolean =>
  (trade ?? "").toLowerCase().includes("new construction");

/**
 * Squares including waste, across every layout on the job.
 *
 * The warranty is priced on what actually goes on the roof, so waste counts.
 */
export function squaresWithWaste(layouts: AutoExpenseInput["layouts"]): number {
  let total = 0;
  for (const layout of layouts) {
    const squares = Number(layout.squares ?? 0);
    if (!Number.isFinite(squares) || squares <= 0) continue;
    const waste = Number(layout.waste_pct ?? 0);
    total += squares * (1 + (Number.isFinite(waste) ? waste : 0) / 100);
  }
  return total;
}

/**
 * Squares of debris coming off the roof — the area times its layers.
 *
 * Deliberately not `tearoffSquares` from the generator, which floors layers at
 * one because a tear-off slot with no layers recorded still means a tear-off.
 * Here zero has to mean zero: new construction and a flute fill genuinely strip
 * nothing, and the dumpster's own floor is what covers them. Rounding a stated
 * zero up to one layer would charge every new build for a tear-off it never had.
 */
export function debrisSquares(layouts: AutoExpenseInput["layouts"]): number {
  let total = 0;
  for (const layout of layouts) {
    const squares = Number(layout.squares ?? 0);
    if (!Number.isFinite(squares) || squares <= 0) continue;
    // Absent still means one. A roof nobody answered the question on is being
    // replaced, which is what the field was for.
    const raw = layout.layers ?? 1;
    const layers = Number(raw);
    total += squares * (Number.isFinite(layers) && layers >= 0 ? layers : 1);
  }
  return total;
}

/**
 * Squares across every layout, waste and all ignored.
 *
 * The building, not the order. Waste is material bought to be thrown away and
 * it does not keep the crane on site an hour longer, so the crane is timed
 * against the roof as measured.
 */
export function roofSquares(layouts: AutoExpenseInput["layouts"]): number {
  let total = 0;
  for (const layout of layouts) {
    const squares = Number(layout.squares ?? 0);
    if (!Number.isFinite(squares) || squares <= 0) continue;
    total += squares;
  }
  return total;
}

/**
 * The GC fee on its own — the ladder needs it as revenue as well as cost.
 *
 * Commercial work only. A general contractor sits between us and the building
 * owner on a commercial job and takes a cut; on a residential job we are
 * dealing with the homeowner directly and there is nobody to pay.
 */
export function gcFee(revenue: Cents, jobType?: string | null): Cents {
  if (!isCommercial(jobType)) return 0 as Cents;
  // No revenue floor any more. An estimate with nothing on it yet is still
  // nothing, though — a $500 minimum on a $0 draft is a fee for no work.
  if (revenue <= 0) return 0 as Cents;
  return cents(
    Math.max(percentOf(revenue, toPercent(GC_FEE_PCT)), GC_FEE_MINIMUM),
  );
}

/**
 * The crews whose hand-added insurance line is covered by a work order here.
 *
 * Jonathon, 2026-09-19: "Use the work order — its entire total, whether the
 * lines were generated or typed. Once a crew has a work order, the typed line
 * stops counting and says the work order covers it." (ADR 0146.)
 *
 * So a crew in this set is charged on its work orders' `documentTotal` —
 * every line on them, generated or typed — and its hand-added line charges
 * nothing. Adding the sub by hand and raising the order later is the ordinary
 * order of events; charging both would budget the same labour twice.
 *
 * The one way this is recognised — by crew id, never by name — used by
 * `insuranceRows` and by the estimate screen alike, so the line that stops
 * counting and the note beside its labour box can never disagree. A work
 * order with no crew yet matches nothing.
 *
 * The order's total does not come into it. Jonathon, 2026-09-20: "A work
 * order will always have a total if there's a work order. If there's a work
 * order then gl and wc need to be generated." So a credit — an order
 * totalling less than nothing — covers its crew like any other, and nets
 * down what that crew's insurance is charged on instead of being ignored.
 * The $0 draft he was asked about the day before covers the crew too, at $0
 * until somebody puts lines on it (ADR 0169, amending ADR 0146).
 */
export function crewsWithBoth(
  workOrders: readonly { crewId?: string | null | undefined }[],
  lineCrewIds: readonly (string | null | undefined)[],
): ReadonlySet<string> {
  const ordered = new Set(
    workOrders.map((o) => o.crewId).filter((id): id is string => !!id),
  );
  return new Set(
    lineCrewIds.filter((id): id is string => !!id && ordered.has(id)),
  );
}

/** What missing paperwork would actually change, policy by policy. */
function assumedCertificateWarning(
  insurance: Pick<OrderInsurance, "glStanding" | "wcStanding">,
  crewName: string,
): string | undefined {
  const missing: string[] = [];
  const savings: string[] = [];

  if (insurance.glStanding === "assumed") {
    missing.push("liability");
    savings.push(
      `a current liability certificate drops liability to $${INSURED_SUB_GL_PER_1000} per $1,000`,
    );
  }
  if (insurance.wcStanding === "assumed") {
    missing.push("workers comp");
    savings.push("a current workers comp certificate removes the comp charge");
  }
  if (missing.length === 0) return undefined;

  return (
    `No current ${missing.join(" or ")} certificate on file for ${crewName}. ` +
    `Budgeted as uninsured — ${savings.join("; ")}.`
  );
}

/**
 * The insurance on the estimate's work orders.
 *
 * One general liability line per crew per class, and a workers comp line
 * beside it only when the company carries the comp. Grouped by crew rather
 * than listed per order because a rep reads this as "what does using Pablo
 * cost me", and because a stable key is what the payment box beside each line
 * is saved against — an extra work order for the same crew should not orphan
 * it.
 *
 * A work order with no crew is still charged. Liability is owed on labour
 * whether or not anybody has said yet who will do it, and dropping the charge
 * would make an unassigned estimate look more profitable than the same job
 * with a crew on it.
 */
function insuranceRows(
  workOrders: readonly AutoExpenseWorkOrder[],
  added: readonly AutoExpenseInsuranceLine[],
  estimateTrade: string | null | undefined,
  today: string,
): AutoExpense[] {
  const covered = crewsWithBoth(
    workOrders,
    added.map((l) => l.crewId),
  );

  interface Group {
    crewId: string | null;
    /** The stored name: what the saved key is built on. */
    crewName: string | null;
    /** The on-screen name: what the line reads. */
    crewLabel: string | null;
    className: string;
    paid: Cents;
    insurance: OrderInsurance;
  }

  const groups = new Map<string, Group>();

  for (const order of workOrders) {
    // Every order, whatever its total. A credit belongs in the crew's base as
    // much as a charge does, and netting it out is the only way the insurance
    // follows what the crew is actually paid (ADR 0169). A crew whose orders
    // come to nothing writes no line, because there is no negative cover.
    const insurance = orderInsurance({
      paid: order.total,
      trades: order.trades ?? null,
      crew: order.crew ?? null,
      today,
    });
    // Crew id, never the display name: two different crews may have the same
    // name, and combining their labour would apply the first one's certificate
    // standing to both. Unassigned orders can share the one explicit bucket.
    const crewIdentity = order.crewId ?? order.crewName ?? "unassigned";
    const key = `${crewIdentity}|${insurance.klass.code}`;
    const seen = groups.get(key);
    if (seen) {
      seen.paid = cents(seen.paid + order.total);
      continue;
    }
    groups.set(key, {
      crewId: order.crewId ?? null,
      crewName: order.crewName ?? null,
      crewLabel: order.crewLabel ?? order.crewName ?? null,
      className: insurance.klass.name,
      paid: order.total,
      insurance,
    });
  }

  // Whether a crew appears in more than one class decides how its lines are
  // named: "General Liability — Pablo" reads fine once and is ambiguous twice.
  const classesPerCrew = new Map<string, number>();
  for (const group of groups.values()) {
    const crew = group.crewName ?? "";
    classesPerCrew.set(crew, (classesPerCrew.get(crew) ?? 0) + 1);
  }

  // Namesakes in the same class also need distinct persisted keys. Keep every
  // existing ordinary key unchanged; add the id only for an actual collision.
  const sameNameAndClass = new Map<string, number>();
  for (const group of groups.values()) {
    const label = `${group.crewName ?? "unassigned"}|${group.insurance.klass.code}`;
    sameNameAndClass.set(label, (sameNameAndClass.get(label) ?? 0) + 1);
  }

  const rows: AutoExpense[] = [];

  for (const group of groups.values()) {
    const { crewId, crewName, crewLabel, className, paid, insurance } = group;
    const split = (classesPerCrew.get(crewName ?? "") ?? 0) > 1;
    const suffix = crewLabel
      ? ` — ${crewLabel}${split ? ` (${className})` : ""}`
      : split
        ? ` (${className})`
        : "";
    const collisionKey = `${crewName ?? "unassigned"}|${insurance.klass.code}`;
    const namesake = (sameNameAndClass.get(collisionKey) ?? 0) > 1;
    const keySuffix =
      `${crewName ?? "unassigned"}${split ? `:${insurance.klass.code}` : ""}` +
      (namesake ? `:${crewId ?? "unassigned"}` : "");

    const liability = percentOf(paid, perThousand(insurance.glPer1000));
    const comp = percentOf(paid, perHundred(insurance.wcPer100));

    const warning = crewLabel
      ? assumedCertificateWarning(insurance, crewLabel)
      : "No crew chosen yet. Budgeted as uninsured until one is — " +
        "an insured sub costs a fraction of this.";

    if (liability > 0) {
      rows.push({
        key: `general_liability:${keySuffix}`,
        name: `General Liability${suffix}`,
        amount: liability,
        basis:
          `$${insurance.glPer1000} per $1,000 of the work order — ` +
          `${className}, ` +
          (insurance.glStanding === "insured"
            ? "insured subcontractor rate"
            : "no certificate on file"),
        ...(warning ? { warning } : {}),
      });
    }

    if (comp > 0) {
      rows.push({
        key: `workers_comp:${keySuffix}`,
        name: `Workers Comp${suffix}`,
        amount: comp,
        basis:
          `$${insurance.wcPer100} per $100 of the work order — ` +
          `${className}, we carry the comp`,
      });
    }
  }

  for (const line of added) {
    const paid = line.paid > 0 ? line.paid : (0 as Cents);
    // The crew's work order covers it — see `crewsWithBoth`. The row stays,
    // at nothing, saying so: it is where the sub and the labour are edited,
    // and a line that vanished the moment somebody raised the order would
    // leave nothing to correct and nothing to delete.
    if (line.crewId && covered.has(line.crewId)) {
      rows.push({
        key: `general_liability:added:${line.id}`,
        name: `General Liability — ${line.crewName?.trim() || "this sub"} (added by hand)`,
        amount: 0 as Cents,
        basis:
          "This sub now has a work order, and its liability and comp are " +
          "charged on the work order's total instead",
        insuranceLineId: line.id,
      });
      continue;
    }

    const trades =
      tradeList(line.trades).length > 0 ? line.trades : estimateTrade;
    const insurance = orderInsurance({
      paid,
      trades: trades ?? null,
      crew: line.crew ?? null,
      today,
    });
    const crewName = line.crewName?.trim() || null;
    // "(added by hand)" on every one of them, whether or not it collides. Two
    // rows reading "General Liability — Pablo" for different money is the
    // thing that has to be impossible; saying which is which only on the
    // estimates where it matters would mean nobody learns to read it.
    const suffix = `${crewName ? ` — ${crewName}` : ""} (added by hand)`;

    const liability = percentOf(paid, perThousand(insurance.glPer1000));
    const comp = percentOf(paid, perHundred(insurance.wcPer100));

    const warning = crewName
      ? assumedCertificateWarning(insurance, crewName)
      : undefined;

    const basis =
      `${formatBasisCents(paid)} of labour, entered by hand — ` +
      `${insurance.klass.name}`;

    // Always pushed, even at nothing. This is the row the labour is typed
    // into, and a line that only appeared once it was already worth something
    // could never be given its first amount. `liability` is zero exactly when
    // `paid` is, so nothing is charged for it either.
    rows.push({
      key: `general_liability:added:${line.id}`,
      name: `General Liability${suffix}`,
      amount: liability,
      basis:
        paid > 0
          ? `$${insurance.glPer1000} per $1,000 of ${basis}, ` +
            (insurance.glStanding === "insured"
              ? "insured subcontractor rate"
              : "no certificate on file")
          : "Say what this sub is being paid and both policies are worked out",
      insuranceLineId: line.id,
      ...(warning && paid > 0 ? { warning } : {}),
    });

    if (comp > 0) {
      rows.push({
        key: `workers_comp:added:${line.id}`,
        name: `Workers Comp${suffix}`,
        amount: comp,
        basis: `$${insurance.wcPer100} per $100 of ${basis}, we carry the comp`,
        insuranceLineId: line.id,
      });
    }
  }

  return rows;
}

/** Every automatic expense for an estimate, in the order they are shown. */
export function computeAutoExpenses(
  input: AutoExpenseInput,
): readonly AutoExpense[] {
  const { revenue, jobType, trade, roofType, layouts, workOrders } = input;
  const rows: AutoExpense[] = [];
  const commercial = isCommercial(jobType);
  const replacement = isRoofReplacement(trade);
  // The permit is the one automatic expense a new build shares with a
  // replacement: the county wants one for a new roof too, so it is charged the
  // same way. The manufacturer warranty stays replacement-only; the shingle on
  // a new build is whatever the template's tier says, and today that is none.
  const permitted = replacement || isNewConstruction(trade);

  // Insurance first, because it is on every job and it is the line the rep is
  // most likely to be asked about.
  rows.push(
    ...insuranceRows(
      workOrders,
      input.insuranceLines ?? [],
      trade,
      input.today ?? todayCentral(),
    ),
  );

  if (permitted) {
    // The same key either way, so an amount typed over the permit, or the
    // permit struck off, holds whichever way it was worked out.
    const permit = input.mountainBrookPermit
      ? mountainBrookPermitFee(input.permitRevenue ?? revenue, jobType)
      : {
          amount: cents(
            Math.max(percentOf(revenue, toPercent(PERMIT_PCT)), PERMIT_MINIMUM),
          ),
          basis: `${PERMIT_PCT}% of revenue, minimum $150`,
        };
    rows.push({ key: "permit", name: "Permit", ...permit });
  }

  if (replacement) {
    const tier = WARRANTY[warrantyTier(input.warranty)];
    const squares = squaresWithWaste(layouts);
    if (tier && squares > 0) {
      rows.push({
        key: "warranty",
        name: tier.name,
        amount: cents(squares * tier.perSquare),
        basis: `$${tier.perSquare / 100} x ${squares.toFixed(
          2,
        )} squares with waste`,
      });
    }
  }

  if (commercial || LIFT_ROOF_TYPES.includes(roofType ?? "")) {
    const lifts = liftCount(roofSquares(layouts));
    rows.push({
      key: "lift",
      name: "Lift",
      amount: multiplyCents(LIFT_COST, lifts),
      basis:
        lifts === 1
          ? commercial
            ? "commercial job"
            : (roofType ?? "metal roof")
          : `${lifts} lifts at $${LIFT_COST / 100} — one per ${LIFT_PER_SQUARES} squares`,
    });
  }

  if (commercial) {
    const debris = debrisSquares(layouts);
    const dumpsters = dumpsterCount(debris);
    rows.push({
      key: "dumpster",
      name: "Dumpster",
      amount: multiplyCents(DUMPSTER_COST, dumpsters),
      basis:
        dumpsters === 1
          ? "commercial job"
          : `${dumpsters} dumpsters at $${DUMPSTER_COST / 100} — ` +
            `${trimNumber(debris)} squares of tear-off, one per ${DUMPSTER_PER_SQUARES}`,
    });
  }

  // Only when somebody has said this job needs one. Everything else in this
  // function is a consequence of the estimate; the crane is a decision.
  if (commercial && input.crane === true) {
    const stated = Number(input.craneDays ?? 0);
    const typed = Number.isFinite(stated) && stated > 0;
    const squares = roofSquares(layouts);
    const days = typed ? stated : craneDaysForSquares(squares);
    rows.push({
      key: "crane",
      name: "Crane",
      amount: craneCost(days),
      basis:
        `${trimNumber(days)} day${days === 1 ? "" : "s"} x ` +
        `${CRANE_MINIMUM_HOURS_PER_DAY} hrs at $${CRANE_HOURLY_RATE / 100}/hr, ` +
        `+${CRANE_FUEL_PCT}% fuel, +${CRANE_MISC_PCT}% misc` +
        (typed
          ? " — days entered by hand"
          : squares > 0
            ? ` — ${trimNumber(squares)} squares at ` +
              `${CRANE_REFERENCE_SQUARES} squares per ${CRANE_REFERENCE_DAYS} days`
            : " — no roof measured yet"),
    });
  }

  const fee = gcFee(revenue, jobType);
  if (fee > 0) {
    rows.push({
      key: "gc_fee",
      name: "GC Fee",
      amount: fee,
      basis: `${GC_FEE_PCT}% of revenue, minimum $${GC_FEE_MINIMUM / 100} — billed on at cost`,
    });
  }

  return applyRemovals(applyOverrides(rows, input.overrides), input.removed);
}

/**
 * A typed amount standing in for the calculated one.
 *
 * Jonathon, 2026-08-31: "remember we always need to be able to edit other
 * expenses." Until now a calculated expense had no amount anybody could touch —
 * the figure is worked out on read rather than saved as a row, so there was
 * nothing on screen to correct. That is right for the rule and wrong for the
 * job in front of you: a lift on a tight site costs what it costs, and the
 * alternative was a second expense line reading "extra lift $500", which
 * reports as a different thing entirely.
 *
 * The calculation still runs. What it produced stays visible in the basis, so
 * an override always shows its own working next to what the rule would have
 * said — an overridden line that nobody can compare against the rule is how a
 * wrong number survives a year.
 *
 * Zero is a real override and has to survive: "this job has no dumpster" is
 * exactly the thing somebody needs to be able to say. So the test is whether a
 * number was given at all, never whether it is truthy.
 */
export function applyOverrides(
  rows: readonly AutoExpense[],
  overrides: AutoExpenseInput["overrides"],
): readonly AutoExpense[] {
  if (!overrides) return rows;
  return rows.map((row) => {
    const amount = overriddenCents(overrides[row.key], row.amount);
    if (amount === row.amount) return row;
    return {
      ...row,
      amount: cents(amount),
      basis: `edited — ${row.basis} would be ${formatBasisCents(row.amount)}`,
    };
  });
}

/**
 * A typed amount if there is a usable one, otherwise the calculated amount.
 *
 * The single place that decides what counts as "somebody typed a number here",
 * because two places decide it — the expense row and the GC fee on the revenue
 * side of the ladder — and they have to agree. Nothing, blank, unreadable or
 * negative all mean "no override": a negative expense is not a correction, it
 * is a typo, and an expense that pays the company back would quietly inflate
 * the margin.
 */
export function overriddenCents(raw: unknown, calculated: Cents): Cents {
  if (raw == null || raw === "") return calculated;
  const amount = Number(raw);
  if (!Number.isFinite(amount) || amount < 0) return calculated;
  return cents(amount);
}

/**
 * The expenses somebody has struck off this job.
 *
 * Jonathon, 2026-09-01, having been given the ability to edit them: "i want to
 * be able to delete the line items too. not just edit." Zeroing one leaves a
 * $0 line on a list of expenses, which reads as an expense that cost nothing
 * rather than one that does not apply — and on a job with four of them the
 * list is mostly noise about things that are not happening.
 *
 * A calculated expense is not a row, so there is nothing to delete. Removal is
 * therefore a fact recorded beside the calculation, exactly as an override is,
 * and the row survives carrying it. Two reasons it survives rather than being
 * filtered out here:
 *
 *   - it can be put back. A rule the whole company shares cannot be re-typed by
 *     hand once it is gone from one job, so a removal with no way back would be
 *     a one-way door on every job it was ever used on.
 *   - the amount stays legible. What the rule would have charged is the number
 *     somebody will want when they ask why this job carried no dumpster.
 *
 * `totalAutoExpenses` is what makes it stop counting. Anything summing these
 * rows must go through it.
 */
export function applyRemovals(
  rows: readonly AutoExpense[],
  removed: AutoExpenseInput["removed"],
): readonly AutoExpense[] {
  if (!removed) return rows;
  return rows.map((row) =>
    isExpenseRemoved(removed[row.key]) ? { ...row, removed: true } : row,
  );
}

/**
 * Whether a stored value means "struck off this job".
 *
 * The column is a boolean and the answer is normally obvious, but the same
 * value arrives from PostgREST, from a test fixture and from an Edge Function,
 * so the test lives in one place. Anything absent, null or false means the
 * expense stands — the default has to be "this job has it", because that is
 * what every job that predates the column is.
 */
export function isExpenseRemoved(raw: unknown): boolean {
  return raw === true || raw === "true" || raw === 1 || raw === "1";
}

/** The expenses this job actually carries — everything not struck off. */
export function activeAutoExpenses(
  rows: readonly AutoExpense[],
): readonly AutoExpense[] {
  return rows.filter((row) => !row.removed);
}

/** Dollars for a basis line. Cents only when there are any. */
function formatBasisCents(amount: Cents): string {
  const dollars = amount / 100;
  return `$${Number.isInteger(dollars) ? dollars.toLocaleString("en-US") : dollars.toFixed(2)}`;
}

/**
 * What the automatic expenses cost this job.
 *
 * Struck-off rows are still in the list — see `applyRemovals` — and are not
 * costs, so they are skipped here. This is the only place that decides it.
 */
export function totalAutoExpenses(rows: readonly AutoExpense[]): Cents {
  return addCents(...activeAutoExpenses(rows).map((r) => r.amount));
}

/** Convenience for callers holding raw numerics. */
export const toExpenseCents = toCents;

/** A quantity for a person to read: 15, not 15.000000000000002. */
const trimNumber = (value: number): string =>
  Number(value.toFixed(2)).toLocaleString("en-US");
